import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCallback, useEffect, useState } from 'react';
import { TVShow, CreateEpisodeRequest, Episode, Language } from '@/types/model';
import { useTranslation } from '@/hooks';
import { Translation } from '@/locales';
import { ContentDetailsSource, contentDetailsService } from '@/services/contentDetailsService';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Toggle from '@/components/ui/Toggle';
import Button from '@/components/ui/Button';
import MultiSelect from '@/components/ui/MultiSelect';
import { Loader2, Plus, Search, Trash2 } from 'lucide-react';
import { languageOptions, qualityOptions, qualityTypes } from '@/constants';
import ImageNotAvailable from '@/assets/image-not-available.png';
import TmdbContentAutocomplete from '@/components/forms/TmdbContentAutocomplete';
import ValidatedEpisodeLinkInput, {
  EpisodeLinkValidationState,
} from '@/components/forms/ValidatedEpisodeLinkInput';
import { suggestPreviousEpisodeLink } from '@/utils/episodeLinkTemplate';

const createEpisodeSchema = (t: Translation) => z.object({
  id: z.string().optional(),
  tvShowId: z.string().min(1, t.validation.required),
  imdbId: z.string().optional(),
  tmdbId: z.string().optional(),
  seasonNumber: z
    .number({ message: t.validation.required })
    .min(1, 'El número de temporada debe ser mayor a 0'),
  episodeNumber: z
    .number({ message: t.validation.required })
    .min(1, 'El número de episodio debe ser mayor a 0'),
  title: z
    .string()
    .min(2, t.validation.minLength(2))
    .max(200, t.validation.maxLength(200)),
  spanishTitle: z.string().max(200, t.validation.maxLength(200)).optional(),
  originalTitle: z.string().max(200, t.validation.maxLength(200)).optional(),
  airDate: z.string().optional(),
  durationMinutes: z
    .number({ message: t.validation.required })
    .optional(),
  rating: z.number().min(0).max(10).optional(),
  spanishSynopsis: z.string().optional(),
  englishSynopsis: z.string().optional(),
  coverImageUrl: z.url().optional().or(z.literal('')),
  active: z.boolean(),
  links: z.array(
    z.object({
      id: z.string().optional(),
      quality: z.enum(qualityTypes),
      url: z.url(t.validation.urlFormat),
      audioLanguages: z.array(z.string()).optional(),
      subtitleLanguages: z.array(z.string()).optional(),
      active: z.boolean(),
    }), t.links.required
  ).min(1, t.links.required)
});

type EpisodeFormData = z.infer<ReturnType<typeof createEpisodeSchema>>;

interface EpisodeFormProps {
  tvShow: TVShow;
  existingEpisodes?: Episode[];
  onSuccess?: (data: CreateEpisodeRequest) => void;
  onCancel?: () => void;
  initialData?: Partial<CreateEpisodeRequest>;
  isEdit?: boolean;
  isSubmitting?: boolean;
  onLinkTVShowToTmdb?: (tmdbId: string) => Promise<void>;
}

const EpisodeForm = ({
                       tvShow,
                       existingEpisodes = [],
                       onSuccess,
                       onCancel,
                       initialData,
                       isEdit = false,
                       isSubmitting = false,
                       onLinkTVShowToTmdb,
                     }: EpisodeFormProps) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [imdbSearchLoading, setImdbSearchLoading] = useState(false);
  const [imdbSearchError, setImdbSearchError] = useState<string | null>(null);
  const [detailsSource, setDetailsSource] = useState<ContentDetailsSource>(
    initialData?.tmdbId ? 'TMDB' : initialData?.imdbId ? 'IMDB' : 'TMDB'
  );
  const [showImdbOnly, setShowImdbOnly] = useState(!isEdit && !initialData?.title);
  const [airDateLoading, setAirDateLoading] = useState(false);
  const [episodePositionLoading, setEpisodePositionLoading] = useState(false);
  const [tvShowTmdbId, setTvShowTmdbId] = useState(tvShow.tmdbId || '');
  const [isTvShowLinked, setIsTvShowLinked] = useState(Boolean(tvShow.tmdbId));
  const [linkValidations, setLinkValidations] = useState<Record<string, EpisodeLinkValidationState>>({});

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm<EpisodeFormData>({
    resolver: zodResolver(createEpisodeSchema(t)),
    defaultValues: {
      id: initialData?.id,
      tvShowId: tvShow.id,
      imdbId: initialData?.imdbId || '',
      tmdbId: initialData?.tmdbId || '',
      seasonNumber: initialData?.seasonNumber || 1,
      episodeNumber: initialData?.episodeNumber || 1,
      title: initialData?.title || '',
      spanishTitle: initialData?.spanishTitle || '',
      originalTitle: initialData?.originalTitle || '',
      airDate: initialData?.airDate || '',
      durationMinutes: initialData?.durationMinutes || 0,
      rating: initialData?.rating || 0,
      spanishSynopsis: initialData?.spanishSynopsis || '',
      englishSynopsis: initialData?.englishSynopsis || '',
      coverImageUrl: initialData?.coverImageUrl || '',
      active: initialData?.active ?? true,
      links: initialData?.links || []
    }
  });

  const searchDetails = async () => {
    const imdbId = watch('imdbId') || '';
    const tmdbId = watch('tmdbId') || '';
    const lookupId = detailsSource === 'IMDB' ? imdbId : tvShowTmdbId;
    if (!lookupId?.trim()) return;

    setImdbSearchLoading(true);
    setImdbSearchError(null);

    try {
      setEpisodePositionLoading(detailsSource === 'IMDB');
      setAirDateLoading(true);
      if (detailsSource === 'TMDB' && !isTvShowLinked && onLinkTVShowToTmdb) {
        await onLinkTVShowToTmdb(tvShowTmdbId);
        setIsTvShowLinked(true);
      }
      const episodeData = await contentDetailsService.getEpisode({
        source: detailsSource,
        imdbId,
        tvShowImdbId: tvShow.imdbId,
        tmdbId,
        tvShowTmdbId,
        seasonNumber: watch('seasonNumber'),
        episodeNumber: watch('episodeNumber'),
      });

      setValue('title', episodeData.title);
      setValue('spanishTitle', episodeData.spanishTitle || '');
      setValue('originalTitle', episodeData.originalTitle || '');
      if (episodeData.imdbId) setValue('imdbId', episodeData.imdbId);
      if (episodeData.tmdbId) setValue('tmdbId', episodeData.tmdbId);
      if (episodeData.durationMinutes) setValue('durationMinutes', episodeData.durationMinutes);
      setValue('englishSynopsis', episodeData.englishSynopsis || '');
      setValue('spanishSynopsis', episodeData.spanishSynopsis || '');
      setValue('coverImageUrl', episodeData.coverImageUrl || '');
      if (episodeData.seasonNumber) setValue('seasonNumber', episodeData.seasonNumber);
      if (episodeData.episodeNumber) setValue('episodeNumber', episodeData.episodeNumber);
      setValue('rating', episodeData.rating ?? 0);
      if (episodeData.airDate) setValue('airDate', episodeData.airDate);

      setShowImdbOnly(false);
    } catch (error) {
      console.error('Content details search error:', error);
      setImdbSearchError(error instanceof Error ? error.message : `Error al buscar en ${detailsSource}`);
    } finally {
      setImdbSearchLoading(false);
      setAirDateLoading(false);
      setEpisodePositionLoading(false);
    }
  };

  const handleImdbSearch = () => {
    searchDetails();
  };

  const onSubmit = async (data: EpisodeFormData) => {
    setSubmitError(null);

    const currentLinkValidations = data.links.map((link) => linkValidations[link.url]);
    if (currentLinkValidations.some((validation) => !validation || validation.status === 'checking')) {
      setSubmitError(t.bulkEpisodes.validationPending);
      return;
    }
    if (currentLinkValidations.some((validation) => validation.status === 'invalid')) {
      setSubmitError(t.bulkEpisodes.invalidLinks);
      return;
    }

    try {
      const formattedData: CreateEpisodeRequest = {
        ...data,
        links: data.links?.map(link => ({
          ...link,
          id: link.id || crypto.randomUUID(),
          audioLanguages: link.audioLanguages as Language[],
          subtitleLanguages: link.subtitleLanguages as Language[],
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString()
        }))
      };
      await onSuccess?.(formattedData);
      reset();
      setShowImdbOnly(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const imdbIdValue = watch('imdbId') || '';
  const canSearchDetails = detailsSource === 'IMDB'
    ? imdbIdValue.trim().length > 0
    : tvShowTmdbId.trim().length > 0;
  const activeValue = watch('active');
  const coverImageValue = watch('coverImageUrl');
  const watchedLinks = watch('links') || [];
  const seasonNumberValue = watch('seasonNumber');
  const episodeNumberValue = watch('episodeNumber');

  useEffect(() => {
    if (
      isEdit
      || watchedLinks.length > 0
      || !seasonNumberValue
      || !episodeNumberValue
    ) {
      return;
    }
    const suggestion = suggestPreviousEpisodeLink(
      existingEpisodes,
      seasonNumberValue,
      episodeNumberValue,
    );
    if (suggestion) {
      setValue('links', [{
        ...suggestion.linkDefaults,
        id: crypto.randomUUID(),
        url: suggestion.url,
        active: true,
      }], { shouldValidate: true });
    }
  }, [
    episodeNumberValue,
    existingEpisodes,
    isEdit,
    seasonNumberValue,
    setValue,
    watchedLinks.length,
  ]);

  const handleAddLink = () => {
    setValue('links', [...watchedLinks, {
      id: crypto.randomUUID(),
      quality: 'FHD',
      url: '',
      audioLanguages: [],
      subtitleLanguages: [],
      active: true,
    }], { shouldDirty: true, shouldValidate: true });
  };

  const handleDeleteLink = (index: number) => {
    setValue(
      'links',
      watchedLinks.filter((_, linkIndex) => linkIndex !== index),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const handleUpdateLink = (
    index: number,
    updates: Partial<EpisodeFormData['links'][number]>,
  ) => {
    setValue(
      'links',
      watchedLinks.map((link, linkIndex) => (
        linkIndex === index ? { ...link, ...updates } : link
      )),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const handleLinkValidationChange = useCallback((
    url: string,
    validation: EpisodeLinkValidationState | undefined,
  ) => {
    setLinkValidations((current) => {
      if (!validation) {
        const next = { ...current };
        delete next[url];
        return next;
      }
      return { ...current, [url]: validation };
    });
  }, []);

  const fillManually = () => {
    setShowImdbOnly(false);
    setImdbSearchError(null);
  };

  if (showImdbOnly) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Crear Nuevo Episodio
          </h2>
          <p className="text-gray-600">
            Serie: <span className="font-medium">{tvShow.title}</span>
          </p>
          <p className="text-gray-600 mt-1">
            Seleccione una fuente e ingrese los datos para completar automáticamente los campos
          </p>
        </div>

        {imdbSearchError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{imdbSearchError}</p>
          </div>
        )}

        <div className="space-y-4">
          <Select
            label="Fuente"
            options={[
              { value: 'TMDB', label: 'TMDB' },
              { value: 'IMDB', label: 'IMDB' },
            ]}
            value={detailsSource}
            onChange={(event) => setDetailsSource(event.target.value as ContentDetailsSource)}
          />
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              {detailsSource === 'IMDB' ? (
                <Input
                  label="ID de IMDB"
                  placeholder="tt1234567 o 1234567"
                  helperText="Ingrese el ID de IMDB y presione Enter o haga clic en buscar"
                  {...register('imdbId')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleImdbSearch();
                    }
                  }}
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {tvShow.tmdbId ? (
                    <Input
                      label="ID TMDB serie"
                      value={tvShowTmdbId}
                      disabled
                    />
                  ) : (
                    <TmdbContentAutocomplete
                      kind="tvShow"
                      value={tvShowTmdbId}
                      selectedTitle={tvShow.title}
                      selectedYear={tvShow.year}
                      label={t.tmdbSearch.linkTvShowLabel}
                      helperText={t.tmdbSearch.episodeLinkHelper}
                      matchCriteria={{ imdbId: tvShow.imdbId, title: tvShow.title, year: tvShow.year }}
                      disabled={isSubmitting || isTvShowLinked}
                      onChange={setTvShowTmdbId}
                    />
                  )}
                  <Input
                    label="Temporada"
                    type="number"
                    min="1"
                    {...register('seasonNumber', { valueAsNumber: true })}
                  />
                  <Input
                    label="Episodio"
                    type="number"
                    min="1"
                    {...register('episodeNumber', { valueAsNumber: true })}
                  />
                </div>
              )}
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                onClick={handleImdbSearch}
                loading={imdbSearchLoading}
                disabled={imdbSearchLoading || !canSearchDetails}
              >
                {imdbSearchLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin"/>
                ) : (
                  <Search className="w-4 h-4"/>
                )}
                Buscar
              </Button>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={fillManually}
              className="w-full"
            >
              Llenar formulario manualmente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEdit ? 'Editar Episodio' : 'Nuevo Episodio'}
            </h2>
            <p className="text-gray-600">
              Serie: <span className="font-medium">{tvShow.title}</span>
            </p>
            <p className="text-gray-600">
              {isEdit ? 'Actualice la información del episodio' : 'Complete todos los campos requeridos'}
            </p>
          </div>
          {!isEdit && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowImdbOnly(true);
                reset();
              }}
              className="flex items-center gap-2"
            >
              <Search className="w-4 h-4"/>
              Buscar detalles
            </Button>
          )}
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="flex gap-6">
          <div className="flex-1 space-y-4">
            <Input
              label="Título del Episodio *"
              placeholder="Título del episodio"
              error={errors.title?.message}
              {...register('title')}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Título en español"
                placeholder="Título localizado"
                error={errors.spanishTitle?.message}
                {...register('spanishTitle')}
              />
              <Input
                label="Título original"
                placeholder="Título original"
                error={errors.originalTitle?.message}
                {...register('originalTitle')}
              />
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Fuente de detalles
              </label>
              <Select
                options={[
                  { value: 'TMDB', label: 'TMDB' },
                  { value: 'IMDB', label: 'IMDB' },
                ]}
                value={detailsSource}
                onChange={(event) => setDetailsSource(event.target.value as ContentDetailsSource)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  ID de IMDB
                </label>
                <input
                  placeholder="tt1234567"
                  {...register('imdbId')}
                  className={`
                    w-full px-3 py-2 border border-gray-300 rounded-md
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                    disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                    ${errors.imdbId ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
                  `}
                />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  ID de TMDB
                </label>
                <input
                  placeholder="ID episodio TMDB"
                  {...register('tmdbId')}
                  className={`
                    w-full px-3 py-2 border border-gray-300 rounded-md
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                    disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                    ${errors.tmdbId ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
                  `}
                />
              </div>
            </div>

            {detailsSource === 'IMDB' ? (
              <div className="space-y-1">
                <div className="flex rounded-md shadow-sm">
                  <input
                    placeholder="tt1234567"
                    value={imdbIdValue}
                    onChange={(event) => setValue('imdbId', event.target.value)}
                    className={`
                      flex-1 px-3 py-2 border border-gray-300 rounded-l-md rounded-r-none border-r-0
                      focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                      disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                      ${errors.imdbId ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
                    `}
                  />
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    className="rounded-l-none border-l-0"
                    onClick={handleImdbSearch}
                    loading={imdbSearchLoading}
                    disabled={imdbSearchLoading || !canSearchDetails}
                  >
                    Actualizar
                  </Button>
                </div>
                {errors.imdbId?.message && (
                  <p className="text-sm text-red-600">
                    {errors.imdbId.message}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-end gap-3">
                <div className="min-w-0 flex-1">
                  {tvShow.tmdbId ? (
                    <Input label="ID TMDB serie" value={tvShowTmdbId} disabled />
                  ) : (
                    <TmdbContentAutocomplete
                      kind="tvShow"
                      value={tvShowTmdbId}
                      selectedTitle={tvShow.title}
                      selectedYear={tvShow.year}
                      label={t.tmdbSearch.linkTvShowLabel}
                      helperText={t.tmdbSearch.episodeLinkHelper}
                      matchCriteria={{ imdbId: tvShow.imdbId, title: tvShow.title, year: tvShow.year }}
                      disabled={isSubmitting || isTvShowLinked}
                      onChange={setTvShowTmdbId}
                    />
                  )}
                </div>
                <Button
                  type="button"
                  onClick={handleImdbSearch}
                  loading={imdbSearchLoading}
                  disabled={imdbSearchLoading || !canSearchDetails}
                >
                  Actualizar
                </Button>
              </div>
            )}

            <Input
              label="Imagen Cover"
              type="url"
              placeholder="https://example.com/episode-cover.jpg"
              error={errors.coverImageUrl?.message}
              {...register('coverImageUrl')}
            />
          </div>

          <div className="flex-shrink-0">
            {coverImageValue ? (
              <img
                src={coverImageValue}
                alt="Vista previa del episodio"
                className="h-58 w-40 object-cover rounded-lg shadow-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = ImageNotAvailable;
                  target.style.display = 'block';
                }}
                onLoad={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'block';
                }}
              />
            ) : (
              <div className="h-58 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <div className="text-xs">Vista previa</div>
                  <div className="text-xs">del episodio</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Input
            label="Número de Temporada *"
            type="number"
            min="1"
            placeholder="1"
            error={errors.seasonNumber?.message}
            helperText={episodePositionLoading ? 'Buscando temporada y episodio en IMDB...' : undefined}
            helperLoading={episodePositionLoading}
            disabled={episodePositionLoading}
            {...register('seasonNumber', { valueAsNumber: true })}
          />

          <Input
            label="Número de Episodio *"
            type="number"
            min="1"
            placeholder="1"
            error={errors.episodeNumber?.message}
            helperText={episodePositionLoading ? 'Buscando temporada y episodio en IMDB...' : undefined}
            helperLoading={episodePositionLoading}
            disabled={episodePositionLoading}
            {...register('episodeNumber', { valueAsNumber: true })}
          />

          <Input
            label="Duración (Minutos)"
            type="number"
            min="0"
            placeholder="45"
            error={errors.durationMinutes?.message}
            {...register('durationMinutes', { valueAsNumber: true })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Fecha de Emisión"
            type="date"
            error={errors.airDate?.message}
            helperText={airDateLoading ? 'Buscando fecha de emisión en IMDB...' : undefined}
            helperLoading={airDateLoading}
            disabled={airDateLoading}
            {...register('airDate')}
          />

          <Input
            label="Rating (0-10)"
            type="number"
            step="0.1"
            min="0"
            max="10"
            placeholder="8.5"
            error={errors.rating?.message}
            {...register('rating', { valueAsNumber: true })}
          />
        </div>

        {/* Synopsis */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sinopsis en Español
            </label>
            <textarea
              placeholder="Sinopsis del episodio en español..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('spanishSynopsis')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sinopsis en Inglés
            </label>
            <textarea
              placeholder="Episode synopsis in English..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('englishSynopsis')}
            />
          </div>
        </div>

        {/* Active Toggle */}
        <div>
          <Toggle
            label="Activo"
            checked={activeValue}
            onChange={(checked) => setValue('active', checked)}
            disabled={isSubmitting}
          />
        </div>

        <section className="border-t border-gray-200 pt-6">
          <div className="mb-4 flex items-center justify-between gap-4">
            <h3 className="font-medium text-gray-900">{t.links.title}</h3>
            <Button
              type="button"
              variant="warning"
              size="sm"
              onClick={handleAddLink}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t.buttons.addLink}
            </Button>
          </div>

          {watchedLinks.length === 0 ? (
            <p className="border-y border-gray-200 py-8 text-center text-sm text-gray-500">
              {t.links.noLinks}
            </p>
          ) : (
            <div className="divide-y divide-gray-200 border-y border-gray-200">
              {watchedLinks.map((link, index) => (
                <div
                  key={link.id || index}
                  className="grid min-w-0 grid-cols-1 gap-4 py-4 md:grid-cols-2 xl:grid-cols-12 xl:items-start"
                >
                  <div className="min-w-0 space-y-1 xl:col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      {t.links.quality}
                    </label>
                    <Select
                      options={qualityOptions}
                      value={link.quality}
                      className="h-10 py-0"
                      onChange={(event) => handleUpdateLink(index, {
                        quality: event.target.value as EpisodeFormData['links'][number]['quality'],
                      })}
                    />
                  </div>
                  <div className="min-w-0 space-y-1 xl:col-span-9">
                    <label className="block text-sm font-medium text-gray-700">
                      {t.links.url}
                    </label>
                    <ValidatedEpisodeLinkInput
                      value={link.url}
                      autoValidate
                      tvShowId={tvShow.id}
                      error={errors.links?.[index]?.url?.message}
                      className="h-10 px-3"
                      onValidationChange={handleLinkValidationChange}
                      onChange={(url) => handleUpdateLink(index, { url })}
                    />
                  </div>
                  <div className="min-w-0 xl:col-span-5">
                    <MultiSelect
                      label={t.links.audioLanguage}
                      options={languageOptions}
                      value={link.audioLanguages || []}
                      onChange={(audioLanguages) => handleUpdateLink(index, {
                        audioLanguages: audioLanguages.map((language) => language.toUpperCase()),
                      })}
                    />
                  </div>
                  <div className="min-w-0 xl:col-span-4">
                    <MultiSelect
                      label={t.links.subtitleLanguage}
                      options={languageOptions}
                      value={link.subtitleLanguages || []}
                      onChange={(subtitleLanguages) => handleUpdateLink(index, {
                        subtitleLanguages: subtitleLanguages.map((language) => language.toUpperCase()),
                      })}
                    />
                  </div>
                  <div className="flex min-w-0 items-center justify-end gap-3 md:col-span-2 xl:col-span-3 xl:pt-6">
                    <div className="mr-auto xl:mr-0">
                      <Toggle
                        label={t.links.active}
                        checked={link.active}
                        onChange={(active) => handleUpdateLink(index, { active })}
                        disabled={isSubmitting}
                      />
                    </div>
                    <button
                      type="button"
                      title={t.links.deleteLink}
                      aria-label={t.links.deleteLink}
                      onClick={() => handleDeleteLink(index)}
                      className="flex h-10 w-10 flex-none cursor-pointer items-center justify-center rounded-md text-red-600 hover:bg-red-50 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {typeof errors.links?.message === 'string' && (
            <p className="mt-2 text-sm text-red-600">{errors.links.message}</p>
          )}
        </section>


        {submitError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}

        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t.buttons.cancel}
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Guardando...'
              : isEdit
                ? 'Actualizar Episodio'
                : 'Crear Episodio'
            }
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EpisodeForm;
